import React, {
  Dispatch, FC, SetStateAction, useState,
} from 'react';
import {
  StyleSheet, Text, FlexStyle, Pressable, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import PressableStyled, { PressableState } from '../PressableStyled';
import Colors from '../../constants/Colors';
import ScrollViewStyled from '../ScrollViewStyled';
import Style from '../../constants/Style';
import DownArrow24 from '../24pxIcons/DownArrow24';
import { Day, DAYS } from '../../screens/LocationManagement/components/BuildingSettingsModal/data';

interface DayDropdownSelectorProps {
  selectedDayIndex: number | null,
  setSelectedDayIndex: Dispatch<SetStateAction<number | null>>,
  style?: FlexStyle,
  disabled?: boolean,
  plural?: boolean,
}

const dayTitle = (day: Day, plural: boolean) => (day ? `${day.title}${plural ? 's' : ''}` : '-');

const DaySelector: FC<DayDropdownSelectorProps> = ({
  selectedDayIndex, setSelectedDayIndex, style, disabled, plural,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const WorkingHours: Day[] = useSelector((state:
    { selectedBuilding: { WorkingHours: Day[] } }) => (state.selectedBuilding.WorkingHours
      ?? DAYS).map((day: Day, index: number) => ({ ...day, index })));
  const dayList: Day[] = WorkingHours ?? DAYS;
  const selectedDay: Day = dayList?.filter((day: Day) => day.index === selectedDayIndex)[0];

  const onItemPress = (index: number): void => {
    setSelectedDayIndex(index);
    setVisible(false);
  };

  return <View {...{ style }}>
    <PressableStyled
      style={[styles.item, styles.button, styles.borders, disabled && styles.buttonDisabled]}
      onPress={() => setVisible(!visible)}
      {...{ disabled }}
    >
      {disabled ? <Text style={styles.text}>{'N/A'}</Text> : <>
        <Text style={styles.text}>
          {dayTitle(selectedDay, plural)}
        </Text>
        <DownArrow24 style={visible ? Style.inverted : Style.upright} />
      </>}
    </PressableStyled>
    {visible ? <ScrollViewStyled style={[styles.dropdown, styles.borders]}>
      {dayList.map((day: Day) => <Pressable
        style={({ hovered, pressed }: PressableState) => [
          styles.item, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
        ]}
        key={`day_selector_option_${day.title}`}
        onPress={() => onItemPress(day.index)}
      >
        <Text style={styles.text}>
          {dayTitle(day, plural)}
        </Text>
      </Pressable>)}
    </ScrollViewStyled> : null}
  </View>;
};

const styles = StyleSheet.create({
  item: {
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  borders: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
    alignSelf: 'flex-end',
    width: 170,
    height: 32,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  text: {
    flex: 1,
    color: Colors.light.hereworksBlack600,
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
  },
  dropdown: {
    backgroundColor: '#fff',
    flex: 1,
    maxHeight: 200,
    width: '100%',
    position: 'absolute',
    top: 36,
    start: 0,
    overflow: 'hidden',
  },
});

export default DaySelector;
